import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PostToSocial SSO callback handler.
 *
 * After the user authenticates on PostToSocial, they're redirected here with
 * ?code=<auth_code>&state=<state>. We:
 *   1) verify the `state` value against what we stored in sessionStorage,
 *   2) POST the code to `/api/auth/sso/exchange` which handles the token
 *      exchange, id_token verification, find-or-create user, and sets our
 *      own session cookie,
 *   3) redirect to /dashboard.
 */
export default function SSOCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("running"); // running | error
  const [error, setError] = useState("");

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const errParam = searchParams.get("error");
    const errDesc = searchParams.get("error_description");

    if (errParam) {
      const msg = errDesc || errParam;
      setError(msg);
      setStatus("error");
      toast.error(`PostToSocial: ${msg}`);
      return;
    }
    if (!code) {
      setError("No authorization code returned from PostToSocial.");
      setStatus("error");
      return;
    }

    const expected = sessionStorage.getItem("sso_state");
    if (state && expected && state !== expected) {
      setError("SSO state mismatch — possible replay or CSRF. Please try signing in again.");
      setStatus("error");
      return;
    }
    // One-shot: clear stored state so the code cannot be re-used
    sessionStorage.removeItem("sso_state");

    (async () => {
      try {
        const res = await fetch(`${API}/auth/sso/exchange`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ code, state }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.detail || `Login failed (${res.status})`);
        }
        toast.success(`Welcome${data.name ? `, ${data.name}` : ""}!`);
        navigate("/dashboard");
      } catch (e) {
        setError(e?.message || "Login failed");
        setStatus("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-slate-200 p-8 text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900">Sign-in couldn&apos;t complete</h1>
          <p className="text-sm text-slate-600" data-testid="sso-error-msg">{error}</p>
          <button
            onClick={() => navigate("/login")}
            className="mt-4 inline-flex items-center px-4 py-2 rounded-md bg-primary text-white hover:opacity-90"
            data-testid="sso-back-to-login-btn"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center space-y-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
        <p className="text-slate-600 text-sm">Finishing sign-in with PostToSocial…</p>
      </div>
    </div>
  );
}
